import type { HTMLChakraProps } from "@chakra-ui/react";
import { chakra, Center } from "@chakra-ui/react";
import { throttle } from "es-toolkit";
import React from "react";
import type { ChangeEvent, FormEvent, FocusEvent } from "react";

import config from "configs/app";
import { useScrollDirection } from "lib/contexts/scrollDirection";
import useIsMobile from "lib/hooks/useIsMobile";
import { useColorModeValue } from "toolkit/chakra/color-mode";
import { Input } from "toolkit/chakra/input";
import { InputGroup } from "toolkit/chakra/input-group";
import { ClearButton } from "toolkit/components/buttons/ClearButton";
import IconSvg from "ui/shared/IconSvg";
interface Props extends Omit<HTMLChakraProps<"form">, "onChange"> {
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onBlur?: (event: FocusEvent<HTMLFormElement>) => void;
  onFocus?: () => void;
  onHide?: () => void;
  onClear: () => void;
  isHomepage?: boolean;
  isSuggestOpen?: boolean;
  value: string;
}

const SearchBarInput = (
  {
    onChange,
    onSubmit,
    isHomepage,
    isSuggestOpen,
    onFocus,
    onBlur,
    onHide,
    onClear,
    value,
    ...rest
  }: Props,
  ref: React.ForwardedRef<HTMLFormElement>,
) => {
  const innerRef = React.useRef<HTMLFormElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLFormElement, []);
  const [isSticky, setIsSticky] = React.useState(false);
  const scrollDirection = useScrollDirection();
  const isMobile = useIsMobile();

  const borderWidthHomepage = useColorModeValue(
    config.UI.homepage.heroBanner?.search?.border_width?.[0] ?? "0px",
    config.UI.homepage.heroBanner?.search?.border_width?.[1] ?? "0px",
  );

  const handleScroll = React.useCallback(() => {
    const TOP_BAR_HEIGHT = 36;
    if (!isHomepage) {
      if (window.scrollY >= TOP_BAR_HEIGHT) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    }
    const clientRect = isMobile && innerRef?.current?.getBoundingClientRect();
    if (clientRect && clientRect.y < TOP_BAR_HEIGHT) {
      onHide?.();
    }
  }, [isMobile, onHide, isHomepage]);

  const handleChange = React.useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  React.useEffect(() => {
    if (!isMobile) {
      return;
    }
    const throttledHandleScroll = throttle(handleScroll, 300);

    window.addEventListener("scroll", throttledHandleScroll);

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [isMobile, handleScroll]);

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isMobile) {
        return;
      }

      switch (event.key) {
        case "/": {
          if (
            ["INPUT", "TEXTAREA"].includes(
              (event.target as HTMLElement).tagName,
            )
          ) {
            break;
          }

          if (!isSuggestOpen) {
            event.preventDefault();
            innerRef.current?.querySelector("input")?.focus();
            onFocus?.();
          }
          break;
        }
        case "Escape": {
          if (isSuggestOpen) {
            innerRef.current?.querySelector("input")?.blur();
            onHide?.();
          }
          break;
        }
      }
    },
    [isMobile, isSuggestOpen, onFocus, onHide],
  );

  React.useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  const transformMobile =
    scrollDirection !== "down" ? "translateY(0)" : "translateY(-100%)";

  const startElement = <IconSvg name="search" boxSize={5} mx={2} />;

  const endElement = (
    <>
      <ClearButton onClick={onClear} visible={value.length > 0} mx={2} />
      {!isMobile && (
        <Center
          boxSize="20px"
          mr={2}
          borderRadius="sm"
          borderWidth="1px"
          borderColor="gray.500"
          color="gray.500"
        >
          /
        </Center>
      )}
    </>
  );

  return (
    <chakra.form
      ref={innerRef}
      noValidate
      onSubmit={onSubmit}
      onBlur={onBlur}
      w="100%"
      backgroundColor="bg.card"
      borderRadius={{ base: isHomepage ? "base" : "none", lg: "base" }}
      position={{ base: isHomepage ? "static" : "absolute", lg: "relative" }}
      top={{ base: isHomepage ? 0 : 55, lg: 0 }}
      left="0"
      zIndex={{
        base: isHomepage ? "auto" : "0",
        lg: isSuggestOpen ? "modal" : "auto",
      }}
      paddingX={{ base: isHomepage ? 0 : 3, lg: 0 }}
      paddingTop={{ base: isHomepage ? 0 : 1, lg: 0 }}
      paddingBottom={{ base: isHomepage ? 0 : 2, lg: 0 }}
      boxShadow={scrollDirection !== "down" && isSticky ? "md" : "none"}
      transform={{ base: isHomepage ? "none" : transformMobile, lg: "none" }}
      transitionProperty="transform,box-shadow,background-color,color,border-color"
      transitionDuration="normal"
      transitionTimingFunction="ease"
      {...rest}
    >
      <InputGroup startElement={startElement} endElement={endElement}>
        <Input
          size="md"
          placeholder={
            isMobile
              ? "Search by address / ... "
              : "Search by address / txn hash / block / token... "
          }
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          borderWidth={isHomepage ? borderWidthHomepage : "2px"}
          borderStyle="solid"
          borderColor={{ _light: "blackAlpha.100", _dark: "whiteAlpha.200" }}
          color={{ _light: "black", _dark: "white" }}
          _hover={{ borderColor: "input.border.hover" }}
          _focusWithin={{
            _placeholder: { color: "gray.300" },
            borderColor: "input.border.focus",
            _hover: { borderColor: "input.border.focus" },
          }}
        />
      </InputGroup>
    </chakra.form>
  );
};

export default React.memo(React.forwardRef(SearchBarInput));
